<script setup lang="ts">
import CashbackMenu from '~/components/sidebar/menu/CashbackMenu.vue';
import Sidebarv2MenuItem from '~/components/sidebarv2/MenuItem.vue';
import Sidebarv2MenuLoyaltyBanner from '~/components/sidebarv2/menu/LoyaltyBanner.vue';
import Sidebarv2MenuRaBanner from '~/components/sidebarv2/menu/RaBanner.vue';
import Sidebarv2SectionTitle from '~/components/sidebarv2/SectionTitle.vue';
import { useLoyaltyHubStore } from '~/stores/loyalty-hub';
import { cashbackPostSlug } from '~/utils/cashback-items';
import { isRunningInNativeApp } from '~/utils/browserInfo';

interface Props {
  isLoggedUser?: boolean;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  onClickMenu: [];
  openRegisterModal: [];
}>();

const route = useRoute();
const { isSidebarMenuOpen, isLayoutMobileOrTablet } = useLayout();
const { isUserRefersComingSoon, isClubFidelidadeEnabled } =
  useFeatureFlagsJson();

const showCashbackMenu = ref(false);
const isCashbackModalOpen = ref(false);
let cashbackBtnRef: HTMLElement | undefined;

const useModalForCashback = isLayoutMobileOrTablet;

const throttledUpdate = () => {
  if (showCashbackMenu.value) updateMenuPosition();
};

const cashbackMenuStyle = ref<any>({ display: 'none' });

const updateMenuPosition = () => {
  if (!cashbackBtnRef) {
    cashbackMenuStyle.value = { display: 'none' };

    return;
  }

  const rect = cashbackBtnRef.getBoundingClientRect();
  const menuWidth = 192;
  const offset = 40;

  let left = rect.left + rect.width + offset;

  if (left + menuWidth > window.innerWidth) {
    left = rect.left - menuWidth - offset;
  }

  cashbackMenuStyle.value = {
    position: 'fixed',
    top: rect.top - 170 + 'px',
    left: left + 10 + 'px',
    zIndex: 10000,
  };
};

watch(showCashbackMenu, (newVal) => {
  if (newVal) {
    nextTick(() => updateMenuPosition());
  }
});

watch(isSidebarMenuOpen, () => {
  if (showCashbackMenu.value) {
    setTimeout(() => updateMenuPosition(), 300);
  }
});

function handleClickOutside() {
  showCashbackMenu.value = false;
}

function openCashback() {
  // Com o Clube ativo, o card abre o CashbackSheet do hub (o mesmo modal que o
  // card "Cashback" dentro do Clube Fidelidade abre), empilhado sem o hub
  // atrás — igual ao openPost do App/Código. O sheet mora no <LoyaltyHub>,
  // montado sob a flag no layout; com a flag desligada não haveria nada pra
  // abrir, então aí segue o CashbackMenu flutuante/modal antigo.
  // Sem emit('onClickMenu'): o sheet abre POR CIMA do menu, que segue aberto
  // no mobile (o menu só fecha quando o clique navega de rota).
  if (isClubFidelidadeEnabled.value) {
    loyaltyHubStore.openCashback();

    return;
  }

  if (useModalForCashback.value) {
    isCashbackModalOpen.value = true;
  } else {
    showCashbackMenu.value = true;
  }
}

function handleCashbackSelect(url: string) {
  showCashbackMenu.value = false;
  isCashbackModalOpen.value = false;
  emit('onClickMenu');

  // Mesmo destino do cashback aberto DENTRO do hub (ver CashbackSheet): o post
  // da promoção abre no PostSheet, empilhado, em vez de jogar o jogador na rota
  // /promotions/<slug>. A rota segue como fallback com a flag desligada, que é
  // quando o <LoyaltyHub> (dono do sheet) não está montado.
  if (isClubFidelidadeEnabled.value) {
    loyaltyHubStore.openPost(cashbackPostSlug(url));

    return;
  }

  navigateTo(url);
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
  document.addEventListener('scroll', throttledUpdate, {
    passive: true,
    capture: true,
  });
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
  document.removeEventListener('scroll', throttledUpdate, { capture: true });
});

// Abertura via URL (?open=cashback) e via shouldOpenCashback (ex.:
// MenuHorizontalApostatudo) é tratada centralmente na layout. Aqui só
// reagimos a clique direto no botão da sidebar (`openCashback`).

function isMenuItemExactActive(path: string) {
  if (!path) return false;

  return route.fullPath === path;
}

const { openLogin } = useAuth();

// Clube de Fidelidade
const loyaltyHubStore = useLoyaltyHubStore();

function handleLoyaltyClick() {
  if (!props.isLoggedUser) {
    openLogin();

    return;
  }
  loyaltyHubStore.open();
  emit('onClickMenu');
}

// Quick action buttons (Indique, Código, App)
type QuickAction = {
  id: string;
  label: string;
  icon: string;
  img: string;
  url: string;
  auth: boolean;
  // Quando presente, o clique abre o post do blog no PostSheet (modal do Club
  // Fidelidade) em vez de navegar pra `url`.
  postSlug?: string;
};

const quickActions: QuickAction[] = [
  {
    id: 'indique',
    label: 'Indique',
    icon: '/assets/images/menu-lateral-grid/refers.webp',
    img: '/assets/images/giratudo/indique.png',
    url: '/user/refers',
    auth: true,
  },
  {
    id: 'codigo',
    label: 'Código',
    icon: '/assets/images/menu-lateral-grid/promocode.webp',
    img: '/assets/images/giratudo/codigo.png',
    url: '/promocode',
    auth: true,
  },
  {
    id: 'app',
    label: 'App',
    icon: '/assets/images/menu-lateral-grid/baixe-app.png',
    img: '/assets/images/giratudo/app.png',
    // Rota antiga, mantida só como referência: o clique abre o modal "Baixe
    // o aplicativo" (ENG-495), ver `handleQuickActionClick`.
    url: '/promotions/aplicativo',
    auth: false,
  },
];

// Cards utilitários do menu aberto (Figma "Sidebar / Utilitário"):
// Cashback · Código · (Aplicativo ou Níveis). Cashback abre o CashbackMenu (o
// card é o `cashbackBtnRef`); Código/Aplicativo reaproveitam
// `handleQuickActionClick`; Níveis abre via `openLevels` (ENG-814).
type UtilityCard = {
  id: 'app' | 'cashback' | 'codigo' | 'niveis';
  label: string;
  img: string;
  // Geometria da ilustração medida do Figma (card de 72x68): caixa
  // width/height em px, `dx` = deslocamento horizontal do centro da arte em
  // relação ao centro do card (o card é flex-1 e fica mais largo que 72 no
  // desktop, então ancoramos pelo centro), `top` a partir do topo do card.
  // As artes NÃO são uniformes — Código é esmagada na horizontal (PNG 3:2
  // renderizado em 69x56), por isso `object-fit: fill`.
  imgBox: { width: number; height: number; dx: number; top: number };
  quickAction?: QuickAction;
};

// ENG-814: dentro do app nativo o card "Aplicativo" não faz sentido (o
// usuário já está nele) e vira "Níveis", sempre por último — mesma posição
// que "Aplicativo" ocupa em web/mobile.
const isNativeApp = isRunningInNativeApp();

const appOrLevelsCard = computed<UtilityCard>(() =>
  isNativeApp
    ? {
        id: 'niveis',
        label: 'Níveis',
        img: '/assets/images/sidebar-v2/util-niveis.webp',
        imgBox: { width: 66.8, height: 45.3, dx: 0, top: 22.8 },
      }
    : {
        id: 'app',
        label: 'Aplicativo',
        img: '/assets/images/sidebar-v2/util-app.webp',
        imgBox: { width: 78.5, height: 78.5, dx: -3.6, top: 22.5 },
        quickAction: quickActions.find((item) => item.id === 'app'),
      },
);

const utilityCards = computed<UtilityCard[]>(() => [
  {
    id: 'cashback',
    label: 'Cashback',
    img: '/assets/images/sidebar-v2/util-cashback.webp',
    imgBox: { width: 76, height: 76, dx: -0.8, top: 19.8 },
  },
  {
    id: 'codigo',
    label: 'Código',
    img: '/assets/images/sidebar-v2/util-codigo.webp',
    imgBox: { width: 69.3, height: 55.8, dx: 0.2, top: 23.5 },
    quickAction: quickActions.find((item) => item.id === 'codigo'),
  },
  appOrLevelsCard.value,
]);

function handleUtilityClick(card: UtilityCard) {
  if (card.id === 'cashback') {
    openCashback();

    return;
  }
  if (card.id === 'niveis') {
    openLevels();

    return;
  }
  if (card.quickAction) handleQuickActionClick(card.quickAction);
}

function openLevels() {
  // Níveis é conteúdo de usuário logado (mesmo gate do Clube em
  // handleLoyaltyClick) — deslogado vai pro login em vez de abrir o modal.
  if (!props.isLoggedUser) {
    openLogin();

    return;
  }

  // Mesmo padrão do openCashback: com o Clube ativo abre o sheet empilhado
  // do hub; com a flag desligada cai no fallback de rota (que segue existindo
  // e fazendo a mesma coisa).
  if (isClubFidelidadeEnabled.value) {
    loyaltyHubStore.openLevels();

    return;
  }

  navigateTo('/vip/levels');
  emit('onClickMenu');
}

function handleQuickActionClick(item: QuickAction) {
  // Indique e Ganhe "Em breve": quick action travado, click desativado.
  if (item.id === 'indique' && isUserRefersComingSoon.value) {
    return;
  }

  if (!props.isLoggedUser && item.auth) {
    openLogin();

    return;
  }
  // Promocode: abre o modal global no lugar de navegar pra /promocode (a rota
  // continua existindo e faz a mesma coisa, mas sem trocar de página).
  // Modais (promocode, app e post) abrem POR CIMA do menu sem fechá-lo no
  // mobile — só o fallback de rota, no fim, fecha o menu.
  if (item.id === 'codigo') {
    usePromocodeStore().open();

    return;
  }

  // App: abre o modal "Baixe o aplicativo" (ENG-495), o mesmo do deep-link
  // ?open=modal-aplicativo. Sem gate de login nem de flag — é convite público.
  if (item.id === 'app') {
    useAppDownloadStore().open();

    return;
  }

  // Quick actions com post do blog abrem no PostSheet (empilhado, sem o hub
  // atrás), em vez de navegar. O sheet mora dentro do <LoyaltyHub>, montado
  // sob a flag no layout — com a flag desligada a rota segue valendo.
  if (item.postSlug && isClubFidelidadeEnabled.value) {
    loyaltyHubStore.openPost(item.postSlug);

    return;
  }

  navigateTo(item.url);
  emit('onClickMenu');
}

// Casino menu items (Atalhos de Cassino)
// Ordem do Figma (node 19771-48065), a mesma no menu aberto e no rail retraído.
const casinoMenuItems = [
  {
    id: 'todos-os-jogos',
    label: 'Todos os jogos',
    url: '/games/category/all',
    icon: 'menu-lateral-v2/todos-os-jogos',
  },
  {
    id: 'promotions',
    label: 'Promoções',
    url: '/promotions',
    icon: 'menu-lateral-v2/promocoes',
  },
  {
    id: 'destaques',
    label: 'Destaques oficiais',
    url: '/perfil-oficial',
    icon: 'menu-lateral-v2/destaques-oficiais',
  },
  {
    id: 'slots',
    label: 'Slots',
    url: '/games/category/all?initialCategory=slots',
    icon: 'menu-lateral-v2/slots',
  },
  {
    id: 'aviators',
    label: 'Aviators',
    url: '/games/category/all?initialCategory=crash',
    icon: 'menu-lateral-v2/aviators',
  },
  {
    id: 'roletas',
    label: 'Roletas',
    url: '/games/category/all?initialCategory=roletas',
    icon: 'menu-lateral-v2/roletas',
  },
  {
    id: 'jogos-ao-vivo',
    label: 'Jogos ao vivo',
    url: '/games/category/all?initialCategory=jogos-ao-vivo',
    icon: 'menu-lateral-v2/jogos-ao-vivo',
  },
  {
    id: 'provedoras',
    label: 'Provedoras',
    url: '/games/providers',
    icon: 'menu-lateral-v2/provedoras',
  },
];

const showNormalGrid = computed(() => isSidebarMenuOpen.value);
const showClosedContent = computed(() => !isSidebarMenuOpen.value);
</script>

<template>
  <!-- PRÊMIO RA1000 (só a partir da abertura das votações) -->
  <div
    v-if="showNormalGrid"
    class="px-[15px] empty:hidden"
  >
    <Sidebarv2MenuRaBanner />
  </div>

  <!-- RA1000 fechado -->
  <div
    v-if="showClosedContent"
    class="w-[70px] empty:hidden"
  >
    <Sidebarv2MenuRaBanner compact />
  </div>

  <!-- CLUBE DE FIDELIDADE BANNER -->
  <div
    v-if="showNormalGrid"
    class="px-[15px]"
  >
    <Sidebarv2MenuLoyaltyBanner @click="handleLoyaltyClick" />
  </div>

  <!-- Clube de Fidelidade fechado -->
  <div
    v-if="showClosedContent"
    class="w-[70px]"
  >
    <div
      v-tooltip.desktopOnly="'Clube de Fidelidade'"
      class="flex justify-center items-center w-[48px] h-[48px] mx-auto rounded-[12px] bg-white/5 hover:bg-white/10 relative cursor-pointer select-none overflow-hidden"
      @click="handleLoyaltyClick"
    >
      <img
        alt="Clube de Fidelidade"
        class="h-[39px] object-contain"
        draggable="false"
        :src="getAssetsUrlForCDN('/assets/images/loyalty-hub/badge-logo.png')"
      />
    </div>
  </div>

  <!-- UTILITÁRIOS (Cashback, Código, Aplicativo/Níveis) -->
  <div
    v-if="showNormalGrid"
    class="utilities-row"
  >
    <button
      v-for="card in utilityCards"
      :key="card.id"
      :ref="
        (el) => {
          if (card.id === 'cashback') cashbackBtnRef = el as HTMLElement;
        }
      "
      class="utility-card"
      :data-cashback-btn="card.id === 'cashback' ? '' : undefined"
      type="button"
      @click.stop="handleUtilityClick(card)"
    >
      <span class="utility-card-label">{{ card.label }}</span>
      <NuxtImg
        :alt="card.label"
        class="utility-card-img"
        draggable="false"
        loading="eager"
        :src="getAssetsUrlForCDN(card.img)"
        :style="{
          width: `${card.imgBox.width}px`,
          height: `${card.imgBox.height}px`,
          top: `${card.imgBox.top}px`,
          '--img-dx': `${card.imgBox.dx}px`,
        }"
      />
      <span class="utility-card-glow" />
    </button>
  </div>

  <!-- Utilitários fechado (mesmos 3 cards do expandido, na mesma ordem) -->
  <div
    v-if="showClosedContent"
    class="flex flex-col gap-[6px] w-[70px]"
  >
    <button
      v-for="card in utilityCards"
      :key="card.id + '-closed'"
      :ref="
        (el) => {
          if (card.id === 'cashback') cashbackBtnRef = el as HTMLElement;
        }
      "
      v-tooltip.desktopOnly="card.label"
      class="flex justify-center items-center w-[48px] h-[48px] mx-auto rounded-[12px] bg-white/5 hover:bg-white/10 relative cursor-pointer select-none overflow-hidden"
      :data-cashback-btn="card.id === 'cashback' ? '' : undefined"
      type="button"
      @click.stop="handleUtilityClick(card)"
    >
      <!-- Sem width/height: com os dois o IPX recorta em "cover" e a arte do
           Código (160x107) perde as laterais. O CSS enquadra o original. -->
      <NuxtImg
        :alt="card.label"
        class="utility-tile-img"
        draggable="false"
        loading="eager"
        :src="getAssetsUrlForCDN(card.img)"
      />
    </button>
  </div>

  <!-- ATALHOS DE CASSINO -->
  <div
    v-if="showNormalGrid"
    class="casino-shortcuts-section"
  >
    <Sidebarv2SectionTitle title="Atalhos de Cassino" />

    <div class="casino-shortcuts-container">
      <template
        v-for="{ id, label, url, icon } in casinoMenuItems"
        :key="id"
      >
        <Sidebarv2MenuItem
          v-tooltip.desktopOnly.closedSideBarOnly="label"
          :draggable="false"
          glow-color="#0606ad"
          :is-active="isMenuItemExactActive(localeLink(url) as string)"
          :to="localeLink(url)"
          @click="emit('onClickMenu')"
        >
          <NuxtIcon
            class="sidebarIcon"
            :name="icon"
          />
          <span>{{ label }}</span>
        </Sidebarv2MenuItem>
      </template>
    </div>
  </div>

  <!-- Atalhos fechado -->
  <div
    v-if="showClosedContent"
    :class="[
      'sidebar-toggle flex flex-col justify-center items-start self-stretch p-0 w-[70px] rounded-none mx-0 border-0',
    ]"
  >
    <template
      v-for="{ id, label, url, icon } in casinoMenuItems"
      :key="id"
    >
      <Sidebarv2MenuItem
        v-tooltip.desktopOnly.closedSideBarOnly="label"
        :draggable="false"
        :is-active="isMenuItemExactActive(localeLink(url) as string)"
        :to="localeLink(url)"
        @click="emit('onClickMenu')"
      >
        <NuxtIcon
          class="sidebarIcon"
          :name="icon"
        />
        <span>{{ label }}</span>
      </Sidebarv2MenuItem>
    </template>
  </div>

  <!-- Menu Cashback desktop -->
  <CashbackMenu
    v-if="showCashbackMenu && !useModalForCashback"
    :is-open="showCashbackMenu"
    :style="cashbackMenuStyle as any"
    @close="showCashbackMenu = false"
    @select-item="handleCashbackSelect"
  />

  <!-- Menu Cashback mobile -->
  <CashbackMenu
    v-else-if="useModalForCashback"
    :is-open="isCashbackModalOpen"
    @close="isCashbackModalOpen = false"
    @select-item="handleCashbackSelect"
  />
</template>

<style lang="scss">
/* Utilitários (Aplicativo · Cashback · Código) */
.utilities-row {
  @apply flex gap-[8px] px-[15px];

  .utility-card {
    @apply relative flex-1 min-w-0 h-[68px] overflow-hidden rounded-[14px] cursor-pointer select-none text-left;
    background-color: rgba(255, 255, 255, 0.06);

    @media (hover: hover) {
      &:hover {
        background-color: rgba(255, 255, 255, 0.1);
      }
    }
  }

  .utility-card-label {
    @apply absolute left-0 right-0 top-[8px] z-10 text-center text-white text-[9px] font-semibold leading-normal whitespace-nowrap;
  }

  .utility-card-img {
    @apply absolute left-1/2 z-10 block max-w-none object-fill pointer-events-none;
    transform: translateX(calc(-50% + var(--img-dx, 0px)));
    filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.4));
  }

  .utility-card-glow {
    @apply absolute left-0 right-0 pointer-events-none;
    top: 42px;
    height: 48px;
    background: radial-gradient(
      ellipse at center,
      rgba(20, 20, 221, 0.5) 0%,
      rgba(20, 20, 221, 0) 70%
    );
    transform: scale(1.8);
  }
}

/* Tile do rail retraído: só a arte do card, sem rótulo. As 3 artes têm
   proporções diferentes (a do Código é mais larga), então cada uma entra
   numa caixa 34x34 com object-contain — respiro igual de 7px nos 4 lados. */
.utility-tile-img {
  @apply block w-[34px] h-[34px] max-w-none object-contain pointer-events-none;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.4));
}

/* Casino Shortcuts Section */
.casino-shortcuts-section {
  @apply px-[15px] flex flex-col gap-[10px];

  .casino-shortcuts-container {
    @apply flex flex-col gap-[6px];
  }
}

.sidebar-toggle {
  @apply border-solid border-b border-sidebar-links/10 select-none;
}
</style>
